import { useEffect, useRef } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { CSSTransition, SwitchTransition } from 'react-transition-group';

import BlockingPhonePopup from '@/components/blocking-phone/blocking-phone-popup';
import OfflineBanner from '@/components/shared/offline-banner';
import TutorialProvider from '@/components/tutorial/tutorial-provider';
import { HOST_MB } from '@/lib/constants';
import { useIsFleetUser } from '@/hooks/api/use-user';
import useIsPWA from '@/hooks/use-is-pwa';

import BottomNav from '@/components/layout/bottom-nav';
import Footer from '@/components/layout/footer';
import Header from '@/components/layout/header';
import Infobulle from '@/components/layout/infobulle';
import Assistance from '@/pages/assistance/assistance-page';
import Avantages from '@/pages/avantages/avantages-page';
import Contact from '@/pages/contact/contact-page';
import Contracts from '@/pages/contract/contract-page';
import Cookies from '@/pages/legal/cookies-page';
import Documents from '@/pages/documents/documents-page';
import Home from '@/pages/home/home-page';
import Mentions from '@/pages/legal/mentions-page';
import NotFound from '@/pages/not-found/not-found-page';
import Paiements from '@/pages/paiements/paiements-page';
import Parrainages from '@/pages/parrainages/parrainages-page';
import PlanDuSite from '@/pages/plan-du-site/plan-du-site-page';
import Profile from '@/pages/profile/profile-page';
import Questions from '@/pages/questions/questions-page';
import Resilier from '@/pages/resiliation/resiliation-page';
import DeclarationSinistre from '@/pages/accident/declaration-sinistre';
import Sinistres from '@/pages/accident/accident-page';

const Page = () => {
  const location = useLocation();
  const nodeRef = useRef<HTMLDivElement>(null);
  const isFrame = window.location.pathname.startsWith('/frames');
  const isPWA = useIsPWA();
  const isFleetUser = useIsFleetUser();

  useEffect(() => {
    window.scrollTo(0, 0);
    const main = document.getElementById('main-content');
    if (main) {
      main.focus({ preventScroll: true });
    }
  }, [location.pathname]);

  return (
    <TutorialProvider>
      <div className="min-h-screen flex flex-col bg-background">
        {/* Offline Banner */}
        <OfflineBanner />

        {/* Skip nav */}
        <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:z-[100] focus:top-2 focus:left-2 focus:bg-primary focus:text-white focus:px-4 focus:py-2 focus:rounded-md">
          Aller au contenu
        </a>

        {/* Header */}
        {!isFrame && <Header />}

        {/* Main Content */}
        <main id="main-content" className={`flex-1 outline-none${isPWA ? ' pb-20' : ''}`} tabIndex={-1}>
          <div className="max-w-7xl mx-auto px-4 py-6">
            <SwitchTransition>
              <CSSTransition
                nodeRef={nodeRef}
                timeout={300}
                addEndListener={(done: () => void) => nodeRef.current?.addEventListener("transitionend", done, false)}
                classNames="fade"
                key={location.pathname}
              >
                <div ref={nodeRef}>
                  <Routes>
                    {isFleetUser ? (
                      <>
                        <Route path="/" element={<Navigate to="/mes-contrats" replace />} />
                        <Route path="/mes-contrats/*" element={<Contracts />} />
                        <Route path="/mes-sinistres/*" element={<Sinistres />} />
                        <Route path="/mon-profil/*" element={<Profile />} />
                        <Route path="/mentions-legales" element={<Mentions />} />
                        <Route path="/gestion-des-cookies" element={<Cookies />} />
                        <Route path="/frames/declaration-de-sinistre" element={<DeclarationSinistre isFrame />} />
                        <Route path="*" element={<Navigate to="/mes-contrats" replace />} />
                      </>
                    ) : (
                      <>
                        <Route path="/" element={<Home />} />
                        <Route path="/mes-contrats/*" element={<Contracts />} />
                        <Route path="/mes-questions" element={<Questions />} />
                        <Route path="/mes-avantages/*" element={<Avantages />} />
                        {HOST_MB === 43397 && <Route path="/mes-parrainages" element={<Parrainages />} />}
                        <Route path="/mes-sinistres/*" element={<Sinistres />} />
                        <Route path="/mentions-legales" element={<Mentions />} />
                        <Route path="/gestion-des-cookies" element={<Cookies />} />
                        <Route path="/plan-du-site" element={<PlanDuSite />} />
                        <Route path="/mon-profil/*" element={<Profile />} />
                        <Route path="/mes-paiements/*" element={<Paiements />} />
                        {(HOST_MB === 43397 || HOST_MB === 111619 || HOST_MB === 479835 || HOST_MB === 479846) && (
                          <Route path="/resilier" element={<Resilier />} />
                        )}
                        <Route path="/contact" element={<Contact />} />
                        <Route path="/assistance" element={<Assistance />} />
                        <Route path="/mes-documents" element={<Documents />} />
                        <Route path="/frames/declaration-de-sinistre" element={<DeclarationSinistre isFrame />} />
                        <Route path="*" element={<NotFound />} />
                      </>
                    )}
                  </Routes>
                </div>
              </CSSTransition>
            </SwitchTransition>
          </div>
        </main>

        {/* Footer & Infobulle */}
        {!isFrame && (
          <>
            {!isFleetUser && <Infobulle />}
            <Footer />
            {isPWA && <BottomNav />}
          </>
        )}
        <BlockingPhonePopup />
      </div>
    </TutorialProvider>
  );
};

export default Page;
